// import * as Sentry from '@sentry/browser'
import { consumerConfig, ApiEnv, ToolType, ok } from '@crc/tools-sdk'
import { post } from '@/utils/ajax'
import Vue from 'vue'
import { sensor } from '@/utils/sensor'

/**
 * 题库的typeId
 */
export const EnumTypeId = {
  ChoiceSingle: 1,
  ChoiceMultiple: 2,
  YesNo: 6,
  Python: 10,
  Kitten: 11,
  Cplusplus: 12,
  Box3D: 13,
  KittenN: 14,
  FillSingle: 20,
  FillMultiple: 21,
  FileUpload: 15
}

/**
 * 赛考的题目类型
 */
export const EnumQuestionType = {
  Kitten: 0,
  PythonUpload: 1,
  Python: 2,
  Scratch: 3,
  Nemo: 4,
  Explore: 5,
  Cplusplus: 6,
  PythonOJ: 7,
  Box3D: 8,
  KittenN: 9,
  PythonRealTimeOJ: 10,
  CplusplusRealTimeOJ: 11,
  Wood2Cloud: 12
}

/**
 * 是否是操作题，根据【typeId】判断
 *
 * 对应值： 10-Python 11-Kitten 12-C++ 13-3DBox 14-Kitten·N
 *
 * @export
 * @param {number} typeId - 题目类型ID（题库那边的存值）
 * @returns
 */
export function isOperateQuestionByTypeId(typeId) {
  return [EnumTypeId.Python, EnumTypeId.Kitten, EnumTypeId.Cplusplus, EnumTypeId.Box3D, EnumTypeId.KittenN].includes(typeId)
}

/**
 * 是否是操作题，根据【questionType】判断
 *
 * 对应值： 0-Kitten 2-Python 3-Scratch 5-Explore 6-C++ 7-Python OJ版 8-3DBox 9-Kitten·N
 *
 * @export
 * @param {number} questionType - 题目类型（赛考这边的存值）
 * @returns
 */
export function isOperateQuestion(questionType) {
  return [
    EnumQuestionType.Kitten,
    EnumQuestionType.Python,
    EnumQuestionType.Scratch,
    EnumQuestionType.Nemo,
    EnumQuestionType.Explore,
    EnumQuestionType.Cplusplus,
    EnumQuestionType.PythonOJ,
    EnumQuestionType.Box3D,
    EnumQuestionType.KittenN,
    EnumQuestionType.PythonRealTimeOJ,
    EnumQuestionType.CplusplusRealTimeOJ,
    EnumQuestionType.Wood2Cloud
  ].includes(questionType)
}

/**
 * 3DBox授权token
 */
export let box3dToken = ''

/**
 * 获取3DBox授权token
 * @returns
 */
export async function get3DBoxToken() {
  try {
    const data = await post('/home/box/login')
    box3dToken = data.token
    return data.token
  } catch (e) {
    Vue.prototype.$message.error('获取3DBox Token失败，请登录后重试')
    // Sentry.captureEvent({
    //   message: '获取3DBox登录token失败',
    //   extra: e && e.message
    // })
    sensor.track('get_3dbox_token_failed', { msg: e && e.message })
  }
}

/**
 * 3DBox获取类型
 */
export const Enum3DBoxType = {
  /** 创建 */
  Create: 'create',
  /** 预览 */
  Preview: 'preview'
}

/**
 * 获取3DBox地址
 *
 * @export
 * @param {Enum3DBoxType} params.type - 获取类型，create: 创建 preview: 预览
 * @param {number} params.count - 轮询查看状态次数
 * @param {number} [params.questionId] - 题目ID，可选，create 时必填
 * @param {number} [params.workId] - 3DBox的mapId，可选，preview 时必填
 * @param {number} params.title - 作品名称
 * @returns {*} { workId, url }
 */
export async function get3DBoxInfo(params) {
  if (!box3dToken) {
    box3dToken = await get3DBoxToken()
  }
  return new Promise((resolve, reject) => {
    generate3DBoxMap({ ...params, resolve, reject })
  })
}

/**
 * 生成3DBox作品
 *
 * @export
 * @param {Enum3DBoxType} options.type - 生成类型，create: 创建 preview: 预览
 * @param {number} options.count - 轮询查看状态次数，默认6次，轮询间隔3秒
 * @param {string} options.requestId - 请求ID，用于轮询查询状态
 * @param {number} options.questionId - 题目ID
 * @param {number} options.workId - 3DBox的mapId
 * @param {number} options.title - 作品名称
 * @param {number} options.resolve - Promise.resolve
 * @param {number} options.reject - Promise.reject
 */
async function generate3DBoxMap(options) {
  let { count = 6, requestId } = options
  const { type, questionId, workId, title, resolve, reject } = options
  let text = ''
  switch (type) {
    case Enum3DBoxType.Create:
      text = '创建'
      break
    case Enum3DBoxType.Preview:
      text = '预览'
      break
  }
  try {
    const params = { requestId, token: box3dToken, title }
    let url = ''
    if (type === Enum3DBoxType.Create) {
      params.questionId = questionId
      url = '/home/box/map/async'
    } else if (type === Enum3DBoxType.Preview) {
      params.workId = workId
      url = '/home/box/map/preview'
    }
    const data = await post(url, params)
    requestId = data.requestId
    if (data.status === 0) { // 生成中，3秒后再去请求接口查看状态
      if (count <= 0) {
        // Sentry.captureEvent({
        //   message: `${text}3DBox作品超时`,
        //   extra: {
        //     questionId,
        //     workId
        //   }
        // })
        if (text === '创建') {
          sensor.track('3dbox_create_timeout', {
            questionId,
            workId
          })
        } else {
          sensor.track('3dbox_preview_timeout', {
            workId
          })
        }
        reject(new Error(`${text}3DBox作品超时`))
      } else {
        setTimeout(() => {
          count--
          generate3DBoxMap({ type, count, questionId, workId, requestId, title, resolve, reject })
        }, 3000)
      }
    } else if (data.status === 1) {
      const info = {
        workId: data.mapInfo.mapId,
        url: `${data.mapInfo.editUrl}?editAutoRun=false&hideAITeacher=true&token=${encodeURIComponent(box3dToken)}`
      }
      resolve(info)
    } else if (data.status === 2) {
      // Sentry.captureEvent({
      //   message: `${text}3DBox作品失败`,
      //   extra: data.msg
      // })
      if (text === '创建') {
        sensor.track('3dbox_create_failed', { msg: data.msg })
      } else {
        sensor.track('3dbox_preview_failed', { msg: data.msg })
      }
      reject(new Error(`${text}3DBox作品失败`))
    }
  } catch (e) {
    // 3dbox授权token失效
    if (e.error_code === 12150002) {
      box3dToken = await get3DBoxToken()
      generate3DBoxMap({ type, count, questionId, workId, requestId, title, resolve, reject })
    } else {
      // Sentry.captureEvent({
      //   message: `${text}3DBox作品失败`,
      //   extra: e && e.message
      // })
      if (text === '创建') {
        sensor.track('3dbox_create_failed', { msg: e && e.message })
      } else {
        sensor.track('3dbox_preview_failed', { msg: e && e.message })
      }
      reject(new Error(`${text}3DBox作品失败`))
    }
  }
}

/**
 * 重置3DBox作品
 *
 * @export
 * @param {number} params.questionId - 生成类型，create: 创建 preview: 预览
 * @param {workId} params.workId - 作品ID，与生成作品的mapId是同一个字段，只是换了个名字
 * @returns {*} { workId, url }
 */
export async function reset3DBoxMap(params) {
  if (!box3dToken) {
    box3dToken = await get3DBoxToken()
  }
  const { questionId, workId } = params
  const data = {
    token: box3dToken,
    questionId,
    workId
  }
  try {
    const res = await post('/home/box/map/reset', data)
    const info = {
      workId: res.workId,
      url: `${res.editUrl}?editAutoRun=false&hideAITeacher=true&token=${encodeURIComponent(box3dToken)}&_=${Date.now()}`
    }
    return info
  } catch (e) {
    // 3dbox授权token失效
    if (e.error_code === 12150002) {
      box3dToken = await get3DBoxToken()
      reset3DBoxMap(params)
    } else {
      // Sentry.captureEvent({
      //   message: '重置3DBox作品失败',
      //   extra: e && e.message
      // })
      sensor.track('3dbox_reset_failed', { msg: e && e.message })
      throw new Error('重置3DBox作品失败')
    }
  }
}

// Kitten·N 环境变量
export const knEnvMap = {
  production: ApiEnv.Production,
  staging: ApiEnv.Staging,
  test: ApiEnv.Test,
  development: ApiEnv.Development
}

/**
 * 初始化Kitten·N
 *
 * @export
 * @param {div} params.container - iframe的容器，一个div dom
 * @param {function} [params.on] - 事件监听，监听来自工具内部的事件，可选
 * @returns {*} sdk对象
 */
export async function initKittenN(params) {
  const { container, on } = params
  try {
    // 传入参数进行初始化配置
    const result = await consumerConfig({
      /**
       * 业务方应用的id
       */
      appId: '2',
      /**
       * 签名。用于鉴权。
       */
      signature: 'abcdefg',
      /**
       * SDK对应的API环境。仅在直接使用webSDK时有效。
       */
      apiEnv: knEnvMap[window.CONFIG.env],
      /**
       * 注入的工具配置项
       */
      toolOptions: {
        /**
         * 工具类型
         */
        type: ToolType.SaikaoKn,
        /**
         * iframe的容器，一个div dom
         */
        container,
        /**
         * 事件监听，监听来自工具内部的事件。
         */
        on: (e) => {
          console.log(e)
          on && on(e)
        },
        /**
         * 工具的配置项，不同的工具可支持的配置项不同。
         */
        configuration: {
          viewMode: 'course'
        }
      }
    })
    if (ok(result)) {
      const sdk = result.body.cSDK
      const isReady = await sdk.application.ready()
      if (ok(isReady)) {
        return sdk
      } else {
        throw new Error(`Kitten·N工具模块初始化失败：${isReady.body ? isReady.body.message : ''}`)
      }
    } else {
      throw new Error(`Kitten·N初始化配置失败：${result.body ? result.body.message : ''}`)
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

const questionTypeIdTextMap = {
  [EnumTypeId.Python]: 'Python',
  [EnumTypeId.Kitten]: 'Kitten',
  [EnumTypeId.Cplusplus]: 'C++',
  [EnumTypeId.Box3D]: '3DBox',
  [EnumTypeId.KittenN]: 'Kitten·N',
  [EnumTypeId.FillSingle]: '填空题',
  [EnumTypeId.FillMultiple]: '多项填空题',
  [EnumTypeId.ChoiceSingle]: '单选题',
  [EnumTypeId.ChoiceMultiple]: '多选题',
  [EnumTypeId.YesNo]: '判断题'
}

const questionTypeTextMap = {
  [EnumQuestionType.Kitten]: 'Kitten',
  [EnumQuestionType.PythonUpload]: 'Python上传题',
  [EnumQuestionType.Python]: 'Python',
  [EnumQuestionType.Scratch]: 'Scratch',
  [EnumQuestionType.Nemo]: 'Nemo',
  [EnumQuestionType.Explore]: 'Explore',
  [EnumQuestionType.Cplusplus]: 'C++',
  [EnumQuestionType.PythonOJ]: 'Python OJ',
  [EnumQuestionType.Box3D]: '3DBox',
  [EnumQuestionType.KittenN]: 'Kitten·N',
  [EnumQuestionType.PythonRealTimeOJ]: 'Python 实时OJ',
  [EnumQuestionType.CplusplusRealTimeOJ]: 'C++ 实时OJ',
  [EnumQuestionType.Wood2Cloud]: 'Wood2Cloud'
}
/**
 * 获取题目类型文本
 *
 * @param {*} questionTypeId 题库的typeId
 * @param {*} questionType 赛考的题目类型
 * @returns 题目类型文本
 */
export function getQuestionTypeText(questionTypeId, questionType) {
  if (questionType !== undefined) {
    return questionTypeTextMap[questionType] || 'unknown'
  } else {
    return questionTypeIdTextMap[questionTypeId] || 'unknown'
  }
}

export function isOperateQuestionByTypeIdAndQuestionType(questionTypeId, questionType) {
  return isOperateQuestion(questionType) || isOperateQuestionByTypeId(questionTypeId)
}
